import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import BoardModal from "./BoardModal";
import ExplorerBoard from "./ExplorerBoard";

const START_FEN =
  "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

const outcomeColour = {
  win: "#2e7d32",
  loss: "#c62828",
  draw: "#757575",
};

// Drop the half/full-move clocks so transpositions match (mirrors the backend).
const normFen = (fen) => fen.split(" ").slice(0, 4).join(" ");

const sideToMove = (fen) => (fen.split(" ")[1] === "b" ? "black" : "white");

function DeviationCell({ game, onHover, onLeave }) {
  if (game.inBook) {
    const lastBook = game.lastBookSan ? (
      <>
        in book through <b>{game.lastBookSan}</b> (move {game.lastBookMoveNumber})
      </>
    ) : (
      <>in book ✓</>
    );
    if (game.opponentDeviated) {
      return (
        <span style={{ color: "#2e7d32" }}>
          {lastBook} — opponent left book: <b>{game.opponentPlayed}</b> (move{" "}
          {game.opponentMoveNumber})
        </span>
      );
    }
    return <span style={{ color: "#2e7d32" }}>{lastBook}</span>;
  }
  return (
    <span
      style={{ cursor: "pointer", color: "#c62828" }}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
    >
      Move {game.deviationMoveNumber}: played <b>{game.deviationPlayed}</b>
      {game.deviationExpected && game.deviationExpected.length > 0 && (
        <> — book: {game.deviationExpected.join(" / ")}</>
      )}
    </span>
  );
}

function DeviationPage() {
  const [games, setGames] = useState([]);
  const [books, setBooks] = useState([]);
  const [bookId, setBookId] = useState(0); // 0 = all books
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [boardModalEl, setBoardModalEl] = useState(null);
  const [hoveredMove, setHoveredMove] = useState(null);
  const [orientation, setOrientation] = useState("white");
  // The line played on the explorer board: [{ san, from, to, fen }].
  const [line, setLine] = useState([]);

  const fetchDeviations = (selectedBookId = bookId) => {
    setLoading(true);
    axios
      .get(`/api/games/deviations`, { params: selectedBookId ? { bookId: selectedBookId } : {} })
      .then((response) => setGames(response.data || []))
      .catch((error) => console.log(error))
      .finally(() => setLoading(false));
  };

  const syncGames = () => {
    setSyncing(true);
    axios
      .post(`/api/games/sync`)
      .then(() => fetchDeviations())
      .catch((error) => console.log(error))
      .finally(() => setSyncing(false));
  };

  useEffect(() => {
    axios
      .get(`/api/book`)
      .then((response) => setBooks(response.data || []))
      .catch((error) => console.log(error));
  }, []);

  useEffect(() => {
    setLine([]); // a new game set invalidates the current line
    fetchDeviations(bookId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId]);

  const currentFen = line.length ? line[line.length - 1].fen : START_FEN;
  const currentKey = normFen(currentFen);

  // Precompute the set of positions each game visited, once per game set.
  const enrichedGames = useMemo(
    () =>
      games.map((game) => {
        const keys = new Set([normFen(START_FEN)]);
        (game.moves || []).forEach((m) => keys.add(normFen(m.fen)));
        return { ...game, positionKeys: keys };
      }),
    [games]
  );

  // Games that reached the position currently on the board.
  const filteredGames = useMemo(
    () => enrichedGames.filter((game) => game.positionKeys.has(currentKey)),
    [enrichedGames, currentKey]
  );

  // Continuations that actually occur in the filtered games, with how many
  // games play each, plus the chessground dest map and a from+to -> fen lookup.
  const { nextMoves, dests, moveToFen } = useMemo(() => {
    const byMove = new Map(); // key: from+to+fen
    for (const game of filteredGames) {
      const moves = game.moves || [];
      for (let i = 0; i < moves.length; i++) {
        const before = i === 0 ? START_FEN : moves[i - 1].fen;
        if (normFen(before) !== currentKey) continue;
        const m = moves[i];
        const key = `${m.from}${m.to}${m.fen}`;
        if (byMove.has(key)) byMove.get(key).count += 1;
        else byMove.set(key, { ...m, count: 1 });
      }
    }
    const list = [...byMove.values()].sort((a, b) => b.count - a.count);
    const destMap = new Map();
    const fenLookup = {};
    for (const m of list) {
      if (!destMap.has(m.from)) destMap.set(m.from, []);
      destMap.get(m.from).push(m.to);
      fenLookup[`${m.from}${m.to}`] = m.fen;
    }
    return { nextMoves: list, dests: destMap, moveToFen: fenLookup };
  }, [filteredGames, currentKey]);

  const playMove = (from, to) => {
    const fen = moveToFen[`${from}${to}`];
    if (!fen) return;
    const played = nextMoves.find((m) => m.from === from && m.to === to);
    setLine((prev) => [...prev, { san: played ? played.san : `${from}${to}`, from, to, fen }]);
  };

  const deviated = filteredGames.filter((g) => !g.inBook).length;

  return (
    <Container>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          margin: "16px 0",
        }}
      >
        <h1>Game Deviations</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <FormControl style={{ minWidth: 220 }}>
            <InputLabel id="book-select-label">Repertoire</InputLabel>
            <Select
              labelId="book-select-label"
              value={bookId}
              onChange={(e) => setBookId(e.target.value)}
            >
              <MenuItem value={0}>All books</MenuItem>
              {books.map((book) => (
                <MenuItem key={book.id} value={book.id}>
                  {book.name} ({book.colour ? "White" : "Black"})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            onClick={syncGames}
            disabled={syncing}
          >
            {syncing ? "Syncing…" : "Sync from chess.com"}
          </Button>
        </div>
      </div>

      {loading ? (
        <CircularProgress />
      ) : games.length === 0 ? (
        <p>No blitz games yet. Click “Sync from chess.com” to fetch your games.</p>
      ) : (
        <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
          <div style={{ width: 360, flexShrink: 0 }}>
            <ExplorerBoard
              fen={currentFen}
              dests={dests}
              turnColor={sideToMove(currentFen)}
              orientation={orientation}
              onMove={playMove}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setLine([])}
                disabled={line.length === 0}
              >
                Reset
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setLine((p) => p.slice(0, -1))}
                disabled={line.length === 0}
              >
                Back
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  setOrientation((o) => (o === "white" ? "black" : "white"))
                }
              >
                Flip
              </Button>
            </div>

            {line.length > 0 && (
              <div style={{ marginTop: 12, fontSize: 14, color: "#333" }}>
                {line.map((m, i) => (
                  <span key={i}>
                    {i % 2 === 0 && <b>{i / 2 + 1}. </b>}
                    {m.san}{" "}
                  </span>
                ))}
              </div>
            )}

            {nextMoves.length > 0 && (
              <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 6 }}>
                {nextMoves.map((m) => (
                  <Chip
                    key={`${m.from}${m.to}${m.fen}`}
                    label={`${m.san} (${m.count})`}
                    onClick={() => playMove(m.from, m.to)}
                    clickable
                    size="small"
                  />
                ))}
              </div>
            )}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <p>
              {filteredGames.length} game{filteredGames.length !== 1 && "s"}
              {line.length > 0 && " reached this position"} — {deviated} left your repertoire.
            </p>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Colour</TableCell>
                  <TableCell>Opponent</TableCell>
                  <TableCell>Result</TableCell>
                  <TableCell>First deviation</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredGames.map((game) => (
                  <TableRow key={game.url}>
                    <TableCell>{game.date}</TableCell>
                    <TableCell>{game.playerWhite ? "White" : "Black"}</TableCell>
                    <TableCell>{game.opponent}</TableCell>
                    <TableCell style={{ color: outcomeColour[game.outcome] }}>
                      {game.outcome}
                    </TableCell>
                    <TableCell>
                      <DeviationCell
                        game={game}
                        onHover={(e) => {
                          setBoardModalEl(e.currentTarget);
                          setHoveredMove({ nextFen: game.deviationFen });
                        }}
                        onLeave={() => {
                          setBoardModalEl(null);
                          setHoveredMove(null);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <a href={game.url} target="_blank" rel="noreferrer">
                        view
                      </a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <BoardModal
        anchorEl={boardModalEl}
        setAnchorEl={setBoardModalEl}
        move={hoveredMove}
      />
    </Container>
  );
}

export default DeviationPage;
